/**
 * TCF API Implementation
 * Implements the __tcfapi as specified by IAB TCF 2.3
 * https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/master/TCFv2/IAB%20Tech%20Lab%20-%20CMP%20API%20v2.md
 */

import { getStoredConsent, getTCString, getACString, hasConsent } from './consent-storage.js';
import { 
  purposes, 
  specialPurposes, 
  features, 
  specialFeatures,
  getVendorCount,
  getAllPurposeIds,
  getAllVendorIds,
  getActiveVendorIds,
  gvlMetadata,
  getFullGVL
} from './gvl-data.js';

// CMP Configuration
// Using a valid CMP ID for testing - IAB allows existing/simulated IDs for validation
// This will be replaced with Captain Compliance's official CMP ID after IAB approval
const CMP_ID = 481; // Captain Compliance registered CMP ID
const CMP_VERSION = 1;
const TCF_POLICY_VERSION = 5; // Must match GVL tcfPolicyVersion

// Event listeners storage
const eventListeners = new Map();
let listenerIdCounter = 0;

// CMP Status
let cmpStatus = 'loading';
let displayStatus = 'hidden';
let gdprApplies = true;

/**
 * Set the CMP status
 */
export function setCmpStatus(status) {
  cmpStatus = status;
}

/**
 * Set the display status
 */
export function setDisplayStatus(status) {
  displayStatus = status;
  // Notify listeners of display status change
  notifyListeners('cmpuishown');
}

/**
 * Notify all registered event listeners
 */
function notifyListeners(eventName) {
  const tcData = buildTCData();
  
  eventListeners.forEach((listener, listenerId) => {
    try {
      listener.callback(tcData, true);
    } catch (e) {
      console.error('Error in TCF event listener:', e);
    }
  });
}

/**
 * Build the TCData object per TCF spec
 */
function buildTCData(vendorIds = null) {
  const storedConsent = getStoredConsent();
  const tcString = getTCString();
  
  // Build purpose consent object
  const purposeConsentsObj = {};
  const purposeLegitimateInterestsObj = {};
  getAllPurposeIds().forEach(id => {
    purposeConsentsObj[id] = storedConsent?.purposeConsents?.has(id) || false;
    purposeLegitimateInterestsObj[id] = storedConsent?.purposeLegitimateInterests?.has(id) || false;
  });
  
  // Build vendor consent object
  const vendorConsentsObj = {};
  const vendorLegitimateInterestsObj = {};
  const vendorIdsToCheck = vendorIds || getAllVendorIds();
  vendorIdsToCheck.forEach(id => {
    vendorConsentsObj[id] = storedConsent?.vendorConsents?.has(id) || false;
    vendorLegitimateInterestsObj[id] = storedConsent?.vendorLegitimateInterests?.has(id) || false;
  });
  
  // Build special feature opt-ins
  const specialFeatureOptinsObj = {};
  [1, 2].forEach(id => {
    specialFeatureOptinsObj[id] = storedConsent?.specialFeatureOptins?.has(id) || false;
  });
  
  // Build publisher restrictions (empty for now)
  const publisherRestrictions = {};
  
  // Build disclosed vendors (same list as the TC string's disclosedVendors segment)
  const disclosedVendorsObj = {};
  getActiveVendorIds().forEach(id => {
    disclosedVendorsObj[id] = true;
  });
  
  return {
    tcString: tcString || '',
    // Google Additional Consent string (ACv2)
    addtlConsent: getACString() || '',
    tcfPolicyVersion: TCF_POLICY_VERSION,
    cmpId: CMP_ID,
    cmpVersion: CMP_VERSION,
    gdprApplies: gdprApplies,
    eventStatus: hasConsent() ? 'useractioncomplete' : 'cmpuishown',
    cmpStatus: cmpStatus,
    listenerId: null,
    isServiceSpecific: true,
    useNonStandardTexts: false,
    publisherCC: 'US',
    purposeOneTreatment: false,
    purpose: {
      consents: purposeConsentsObj,
      legitimateInterests: purposeLegitimateInterestsObj
    },
    vendor: {
      consents: vendorConsentsObj,
      legitimateInterests: vendorLegitimateInterestsObj
    },
    specialFeatureOptins: specialFeatureOptinsObj,
    publisher: {
      consents: {},
      legitimateInterests: {},
      customPurpose: {
        consents: {},
        legitimateInterests: {}
      },
      restrictions: publisherRestrictions
    },
    // Out of band consents - disclosed vendors
    outOfBand: {
      allowedVendors: {},
      disclosedVendors: disclosedVendorsObj
    }
  };
}

/**
 * Handle the 'ping' command
 */
function handlePing(callback) {
  const pingData = {
    gdprApplies: gdprApplies,
    cmpLoaded: true,
    cmpStatus: cmpStatus,
    displayStatus: displayStatus,
    apiVersion: '2.3',
    cmpVersion: CMP_VERSION,
    cmpId: CMP_ID,
    gvlVersion: gvlMetadata.vendorListVersion,
    tcfPolicyVersion: TCF_POLICY_VERSION
  };
  
  callback(pingData, true);
}

/**
 * Handle the 'getTCData' command
 */
function handleGetTCData(callback, vendorIds = null) {
  const tcData = buildTCData(vendorIds);
  callback(tcData, true);
}

/**
 * Handle the 'addEventListener' command
 */
function handleAddEventListener(callback) {
  const listenerId = ++listenerIdCounter;
  
  eventListeners.set(listenerId, {
    callback: callback
  });
  
  // Immediately call with current state
  const tcData = buildTCData();
  tcData.listenerId = listenerId;
  callback(tcData, true);
}

/**
 * Handle the 'removeEventListener' command
 */
function handleRemoveEventListener(callback, listenerId) {
  const removed = eventListeners.delete(listenerId);
  callback(removed, true);
}

/**
 * Handle the 'getInAppTCData' command (for mobile apps)
 */
function handleGetInAppTCData(callback) {
  // Same as getTCData for web
  handleGetTCData(callback);
}

/**
 * Handle the 'getVendorList' command
 */
function handleGetVendorList(callback, vendorListVersion = null) {
  const gvl = getFullGVL();
  callback(gvl, true);
}

/**
 * The main __tcfapi function
 * This is the global API that vendors will call
 */
function tcfapi(command, version, callback, parameter) {
  if (typeof callback !== 'function') {
    console.error('TCF API: callback must be a function');
    return;
  }
  
  // Version check - we support TCF 2.x
  if (version !== 2) {
    callback(null, false);
    return;
  }
  
  switch (command) {
    case 'ping':
      handlePing(callback);
      break;
      
    case 'getTCData':
      handleGetTCData(callback, parameter);
      break;
      
    case 'addEventListener':
      handleAddEventListener(callback);
      break;
      
    case 'removeEventListener':
      handleRemoveEventListener(callback, parameter);
      break;
      
    case 'getInAppTCData':
      handleGetInAppTCData(callback);
      break;
      
    case 'getVendorList':
      handleGetVendorList(callback, parameter);
      break;
      
    default:
      console.warn(`TCF API: Unknown command "${command}"`);
      callback(null, false);
  }
}

/**
 * Initialize the TCF API on the window object
 */
export function initTCFApi() {
  // Create the __tcfapi function
  window.__tcfapi = tcfapi;
  
  // Create the __tcfapiLocator iframe for cross-origin communication
  if (!document.getElementById('__tcfapiLocator')) {
    const iframe = document.createElement('iframe');
    iframe.name = '__tcfapiLocator';
    iframe.id = '__tcfapiLocator';
    iframe.style.display = 'none';
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);
  }
  
  // Handle postMessage for cross-origin iframe communication
  window.addEventListener('message', handlePostMessage, false);
  
  // Process any queued commands
  processCommandQueue();
  
  // Set CMP as loaded
  cmpStatus = 'loaded';
  
  console.log('TCF API initialized');
}

/**
 * Handle postMessage events for cross-origin iframe communication
 */
function handlePostMessage(event) {
  let data;
  
  try {
    data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
  } catch (e) {
    return; // Not a JSON message
  }
  
  if (!data || !data.__tcfapiCall) {
    return;
  }
  
  const { callId, command, version, parameter } = data.__tcfapiCall;
  
  tcfapi(command, version, (returnValue, success) => {
    const response = {
      __tcfapiReturn: {
        returnValue,
        success,
        callId
      }
    };
    
    if (event.source) {
      event.source.postMessage(JSON.stringify(response), '*');
    }
  }, parameter);
}

/**
 * Process any commands that were queued before the API was ready
 */
function processCommandQueue() {
  // Check for command queue (stub implementation pattern)
  if (window.__tcfapi && window.__tcfapi.queue) {
    const queue = window.__tcfapi.queue;
    queue.forEach(args => {
      tcfapi.apply(null, args);
    });
  }
}

/**
 * Notify all listeners that consent has been updated
 */
export function notifyConsentUpdate() {
  notifyListeners('tcloaded');
}

/**
 * Set GDPR applies flag
 */
export function setGdprApplies(applies) {
  gdprApplies = applies;
}

/**
 * Get the current CMP status
 */
export function getCmpStatus() {
  return cmpStatus;
}

/**
 * Get the current display status
 */
export function getDisplayStatus() {
  return displayStatus;
}



